import { IFileState, systemRights } from "@tedivo/tvd-api-models";
import {
  ITedivoShoelaceSelectOptions,
  createSelectShoelace,
} from "@tedivo/tedivo-form";
import {
  TDownloadType,
  createStafFileBlobWithDialog,
} from "./edits/helpers/export/createStafFileBlobWithDialog";

import { ViewJsonTypeEnum } from "../ViewJsonTypeEnum";
import { cloneFileDialog } from "../../my-cloud/cloneFileDialog";
import { createSimpleAlert } from "@tedivo/tedivo-ui";
import { getTranslation } from "../../../../app/i18/i18tn";
import ovdJsonStore from "../../../../app/stores/OVDJsonStore";
import { routeFns } from "../../../../app/router/routes";
import router from "../../../../app/router";
import securityModule from "../../../../app/security/SecurityModule";
import stafSaveOptions from "../../../../app/stores/stafSaveOptions";
import { startDownloadInBrowser } from "@tedivo/tedivo-dom-helpers";

export function createDownloadOVDJsonLink(
  viewType: ViewJsonTypeEnum,
  fileOrgId: string,
  fileId: string,
  currentFileState: IFileState,
): HTMLElement | null {
  const {
    currentJson: json,
    filenameSanitized: name,
    filenameSanitizedForSE,
    lcgVcgTcgAndPairings,
  } = ovdJsonStore;

  if (securityModule.planIsReadOnly) {
    return null;
  }

  const userIsTedivoAdmin = securityModule.userIsTedivoAdmin;

  if (currentFileState === "DRAFT" && !userIsTedivoAdmin) {
    return createNoDownloadButton();
  }

  const cgsStats = lcgVcgTcgAndPairings?.cgStats;

  const options: TDownloadOptions = [
    // A. OVD JSON
    canDownloadJsonOrStaf(
      systemRights.ORG.DownloadOwnOrgFileAsOvd,
      fileOrgId,
    ) || userIsTedivoAdmin
      ? {
          name: getTranslation("view:download.downloadOVDJson"),
          value: "ovdJson",
          icon: "download",
        }
      : undefined,
    // B. STAF
    canDownloadJsonOrStaf(
      systemRights.ORG.DownloadOwnOrgFileAsStaf,
      fileOrgId,
    ) || userIsTedivoAdmin
      ? {
          name: getTranslation("view:download.downloadSTAF"),
          value: "stafFile",
          icon: "download",
        }
      : undefined,
    // C. Clone to my Account
    viewType === ViewJsonTypeEnum.VIEW_OVD &&
    (securityModule.userHasPermission(systemRights.ORG.CanDownloadPublicFile) ||
      userIsTedivoAdmin) &&
    currentFileState === "COMMUNITY"
      ? {
          name: getTranslation("general:search.cloneItToMyAccount"),
          value: "cloneToMyAccount",
          icon: "cloud-download-fill",
        }
      : undefined,
  ].filter(Boolean) as TDownloadOptions;

  if (options.length === 0) {
    return document.createElement("span");
  }

  const modal = document.createElement("sl-dialog");

  const downloadSelect = createSelectShoelace({
    id: "downloadBtn",
    caret: false,
    pill: true,
    ommitCheckSign: true,
    size: "small",
    variant: "primary",
    buttonText: getTranslation("view:download.title"),
    iconPrefix: "download",
    options,
    onChange: async (v: TDownloadType) => {
      let blob: Blob | undefined = undefined;
      let url: string | undefined = undefined;
      if (!json) return;

      switch (v) {
        case "ovdJson":
          // Directly download the OVD JSON
          blob = new Blob(
            [
              JSON.stringify(
                json,
                null,
                stafSaveOptions.isFormattedJsonSaved ? 2 : 0,
              ),
            ],
            {
              type: "application/json",
            },
          );

          url = blob ? URL.createObjectURL(blob) : undefined;
          startDownloadInBrowser(url, `${name}.json`);

          break;
        case "stafFile":
          // Open the modal to download the STAF file
          blob = await createStafFileBlobWithDialog(json, cgsStats);

          url = blob ? URL.createObjectURL(blob) : undefined;
          startDownloadInBrowser(url, `${filenameSanitizedForSE}.txt`);

          break;
        case "cloneToMyAccount":
          cloneFileDialog({
            onlyCommunity: true,
            actionLabel: "general:search.cloneItToMyAccount",
            goSquaredLabel: "TVL - Clone file",
            modal: modal,
            fileData: {
              name: json?.shipData.shipName || "",
              fileId: fileId,
              organizationId: fileOrgId,
            },
          });
          break;
        case "compare":
          {
            const version = router.getRouteParams().version;
            if (!version) return;
            const route = routeFns.fileCompareOwnVersion(fileId, version);
            router.navigate(route, { organizationId: fileOrgId });
          }
          break;
      }
    },
  });

  const btnHolder = document.createElement("div");
  btnHolder.appendChild(downloadSelect);
  btnHolder.appendChild(modal);

  return btnHolder;

  function canDownloadJsonOrStaf(right: string, orgId: string) {
    const isSameOrg = orgId === securityModule.currentOrganizationId;
    return (
      currentFileState !== "DRAFT" &&
      securityModule.userHasPermission(right) &&
      isSameOrg
    );
  }
}

function createNoDownloadButton() {
  const btn = document.createElement("sl-button");
  btn.size = "small";
  btn.outline = true;
  btn.pill = true;

  const icon = document.createElement("sl-icon");
  icon.name = "download";
  icon.slot = "prefix";

  btn.variant = "neutral";
  btn.innerHTML = getTranslation("view:download.title");
  btn.appendChild(icon);

  btn.addEventListener("click", () => {
    createSimpleAlert({
      title: getTranslation("view:download.downloadNotPossible"),
      message: getTranslation("view:download.downloadNotPossibleText"),
      onOk: () => null,
      closeLabel: getTranslation("general:common.ok"),
    });
  });

  return btn;
}

type TDownloadOptions = Array<
  ITedivoShoelaceSelectOptions & { value: TDownloadType }
>;
